import type { Series } from '../../types/domain.js'
import { isBroadcastableMedia } from '../../lib/utils.js'

/**
 * Lectures dérivées de la fiche série.
 *
 * La maquette Pen affiche une durée cible, une périodicité et un score de
 * cohérence qui n'existent pas dans le modèle `Series` : plutôt que d'inventer
 * des colonnes, on les déduit des épisodes déjà chargés par `GET /series`.
 * Tout est pur et testable — aucune de ces valeurs n'est écrite en base.
 */

export type SeriesEpisode = NonNullable<Series['programs']>[number]

/** Date de référence d'un épisode : prévue, puis constatée, puis création. */
export function episodeAirDate(episode: SeriesEpisode): string | null {
  const candidate = episode as SeriesEpisode & {
    plannedFirstAirDate?: string | null
    firstAiredAt?: string | null
    createdAt?: string | null
  }
  return candidate.plannedFirstAirDate ?? candidate.firstAiredAt ?? candidate.createdAt ?? null
}

/**
 * Durée réellement posée à l'antenne : le programme, ses parties, et les
 * coupures qui les séparent. La durée de la partie 1 seule sous-estimerait une
 * émission découpée.
 */
export function episodeTotalDurationSec(episode: SeriesEpisode): number {
  const parts = episode.parts ?? []
  const partsDuration = parts.reduce((total, part) => total + part.durationSec, 0)
  return episode.durationSec + partsDuration + episode.interPartBreakSec * parts.length
}

/** Un épisode est diffusable quand lui ET chacune de ses parties ont un média. */
export function episodeMediaReady(episode: SeriesEpisode): boolean {
  return [episode, ...(episode.parts ?? [])].every(part =>
    part.mediaAssets?.some(asset => isBroadcastableMedia(asset.status)),
  )
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1
    ? sorted[middle]
    : Math.round((sorted[middle - 1] + sorted[middle]) / 2)
}

/**
 * Durée cible de la série : la médiane des épisodes, pas la moyenne. Un pilote
 * de 90 minutes au milieu d'un quotidien de 26 déplacerait la moyenne et ferait
 * passer toute la saison pour « hors cible ».
 */
export function seriesTargetDurationSec(episodes: SeriesEpisode[]): number | null {
  return median(episodes.map(episodeTotalDurationSec).filter(value => value > 0))
}

/** Écart médian entre deux diffusions, en jours. */
export function seriesMedianGapDays(episodes: SeriesEpisode[]): number | null {
  const times = episodes
    .map(episodeAirDate)
    .filter((value): value is string => Boolean(value))
    .map(value => new Date(value).getTime())
    .filter(value => Number.isFinite(value))
    .sort((a, b) => a - b)
  if (times.length < 2) return null
  const gaps: number[] = []
  for (let index = 1; index < times.length; index += 1) {
    const days = (times[index] - times[index - 1]) / 86_400_000
    if (days > 0) gaps.push(days)
  }
  return median(gaps)
}

/**
 * Périodicité éditoriale déduite de l'écart médian. Les bornes sont larges à
 * dessein : une série quotidienne saute les week-ends et les jours fériés, une
 * hebdomadaire glisse d'un jour ou deux sans changer de nature.
 */
export function seriesPeriodicityLabel(episodes: SeriesEpisode[]): string {
  const gap = seriesMedianGapDays(episodes)
  if (gap == null) return 'Non déterminée'
  if (gap <= 1.6) return 'Quotidienne'
  if (gap <= 4)   return 'Pluri-hebdomadaire'
  if (gap <= 10)  return 'Hebdomadaire'
  if (gap <= 20)  return 'Bimensuelle'
  if (gap <= 45)  return 'Mensuelle'
  return 'Occasionnelle'
}

/** Saisons présentes dans la série, de la plus récente à la plus ancienne. */
export function seriesSeasons(episodes: SeriesEpisode[]): number[] {
  const seasons = new Set<number>()
  for (const episode of episodes) {
    if (episode.season != null) seasons.add(episode.season)
  }
  return [...seasons].sort((a, b) => b - a)
}

export function episodesOfSeason(episodes: SeriesEpisode[], season: number | null): SeriesEpisode[] {
  if (season == null) return episodes
  return episodes.filter(episode => episode.season === season)
}

/** Bornes de diffusion d'une saison, pour le sous-titre « septembre — juin ». */
export function seasonRangeLabel(episodes: SeriesEpisode[]): string | null {
  const dates = episodes
    .map(episodeAirDate)
    .filter((value): value is string => Boolean(value))
    .map(value => new Date(value))
    .filter(date => Number.isFinite(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime())
  if (dates.length === 0) return null
  const format = (date: Date) => date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
  const first = format(dates[0])
  const last  = format(dates[dates.length - 1])
  return first === last ? first : `${first} — ${last}`
}

export interface SeriesCoherenceCheck {
  id:    string
  label: string
  tone:  'ok' | 'warning' | 'danger'
}

export interface SeriesCoherence {
  /** Pourcentage d'épisodes sans anomalie, arrondi. `null` sans épisode. */
  score:  number | null
  checks: SeriesCoherenceCheck[]
}

/** Un épisode s'écarte de la cible au-delà de 5 %, plancher à 30 secondes. */
export function isOffTarget(durationSec: number, targetSec: number): boolean {
  const tolerance = Math.max(30, Math.round(targetSec * 0.05))
  return Math.abs(durationSec - targetSec) > tolerance
}

/**
 * Contrôles éditoriaux de la série. Le score compte les épisodes sans aucune
 * anomalie : un épisode cumulant deux défauts ne pénalise pas deux fois, sinon
 * une série courte tomberait sous zéro.
 */
export function seriesCoherence(series: Series, episodes: SeriesEpisode[]): SeriesCoherence {
  if (episodes.length === 0) {
    return {
      score: null,
      checks: [{ id: 'empty', label: 'Aucun épisode rattaché', tone: 'warning' }],
    }
  }

  const target = seriesTargetDurationSec(episodes)
  const named       = episodes.filter(episode => episode.title.trim().length > 0)
  const offTarget   = target == null ? [] : episodes.filter(episode => isOffTarget(episodeTotalDurationSec(episode), target))
  const missingMedia = episodes.filter(episode => !episodeMediaReady(episode))
  const unnumbered  = series.numberEpisodes
    ? episodes.filter(episode => episode.season == null || episode.episode == null)
    : []

  const flagged = new Set<string>()
  for (const episode of [...offTarget, ...missingMedia, ...unnumbered]) flagged.add(episode.id)
  for (const episode of episodes) {
    if (episode.title.trim().length === 0) flagged.add(episode.id)
  }

  const checks: SeriesCoherenceCheck[] = [
    {
      id: 'named',
      label: named.length === episodes.length
        ? `${episodes.length} épisode${episodes.length > 1 ? 's' : ''} nommé${episodes.length > 1 ? 's' : ''}`
        : `${episodes.length - named.length} épisode${episodes.length - named.length > 1 ? 's' : ''} sans titre`,
      tone: named.length === episodes.length ? 'ok' : 'warning',
    },
    {
      id: 'duration',
      label: target == null
        ? 'Durée cible indéterminée'
        : offTarget.length === 0
          ? 'Durées conformes à la cible'
          : `${offTarget.length} durée${offTarget.length > 1 ? 's' : ''} hors cible`,
      tone: target == null ? 'warning' : offTarget.length === 0 ? 'ok' : 'warning',
    },
    {
      id: 'media',
      label: missingMedia.length === 0
        ? 'Médias disponibles'
        : `${missingMedia.length} épisode${missingMedia.length > 1 ? 's' : ''} sans média`,
      tone: missingMedia.length === 0 ? 'ok' : 'danger',
    },
  ]

  if (series.numberEpisodes) {
    checks.push({
      id: 'numbering',
      label: unnumbered.length === 0
        ? 'Numérotation complète'
        : `${unnumbered.length} épisode${unnumbered.length > 1 ? 's' : ''} sans numéro`,
      tone: unnumbered.length === 0 ? 'ok' : 'warning',
    })
  }

  return {
    score: Math.round(((episodes.length - flagged.size) / episodes.length) * 100),
    checks,
  }
}

export type SeriesEpisodeState = 'PRÊT' | 'COMPLET' | 'À VÉRIFIER' | 'SANS MÉDIA'

/**
 * État affiché dans la liste des épisodes.
 *
 * `PRÊT` distingue la prochaine diffusion programmée ; `À VÉRIFIER` signale une
 * durée qui s'écarte de la cible alors que le média est là — c'est de
 * l'éditorial, pas une panne technique.
 */
export function episodeState(
  episode: SeriesEpisode,
  targetSec: number | null,
  options: { upcoming?: boolean } = {},
): SeriesEpisodeState {
  if (!episodeMediaReady(episode)) return 'SANS MÉDIA'
  if (targetSec != null && isOffTarget(episodeTotalDurationSec(episode), targetSec)) return 'À VÉRIFIER'
  return options.upcoming ? 'PRÊT' : 'COMPLET'
}

/** Agrégats de la barre d'outils : séries, épisodes, séries actives. */
export function seriesCatalogTotals(list: Series[]): { series: number; episodes: number; active: number } {
  return {
    series:   list.length,
    episodes: list.reduce((total, item) => total + (item._count?.programs ?? item.programs?.length ?? 0), 0),
    active:   list.filter(item => item.active).length,
  }
}

/**
 * Prochaine diffusion de la liste : le premier épisode encore à venir.
 *
 * `now` est injecté plutôt que lu depuis l'horloge : c'est ce qui rend la
 * règle testable, et ce qui évite qu'un rendu et sa vérification tombent de
 * part et d'autre de minuit.
 */
export function nextUpcomingEpisodeId(episodes: SeriesEpisode[], now: number): string | null {
  const future = episodes
    .map(episode => ({ id: episode.id, time: new Date(episodeAirDate(episode) ?? '').getTime() }))
    .filter(entry => Number.isFinite(entry.time) && entry.time >= now)
    .sort((a, b) => a.time - b.time)
  return future[0]?.id ?? null
}

/**
 * Date de diffusion affichée dans la liste des épisodes. Le jour courant est
 * nommé plutôt que daté : c'est la ligne que l'opérateur cherche en premier.
 */
export function formatAirDate(value: string | null, now: number): string {
  if (!value) return '—'
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return '—'
  const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  if (date.toDateString() === new Date(now).toDateString()) return `Aujourd’hui · ${time}`
  return `${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · ${time}`
}

/**
 * Ordre d'affichage des épisodes.
 *
 * `filterEpisodes` trie toujours du plus ancien au plus récent, mais la série
 * porte son propre choix dans `episodeSortDescending` — un quotidien se lit à
 * l'envers, la dernière édition en tête. Appliquer l'ordre ici plutôt que dans
 * le filtre garde la recherche et l'ordre séparés.
 */
export function orderEpisodesForDisplay<T>(ascending: T[], descending: boolean): T[] {
  return descending ? [...ascending].reverse() : ascending
}
